import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import WizardStep from '../WizardStep';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, Eye, ShieldCheck, Undo2 } from 'lucide-react';
import { Spinner } from '@/components/ui/spinner';
import { useAuth } from '@/contexts/AuthContext';
import { getGoogleClientId } from '@/lib/googleClientId';

interface ConnectGoogleStepProps {
  onNext: () => void;
  onBack: () => void;
  onGoogleAccountCreated: (accountId: string) => void;
}

const ConnectGoogleStep: React.FC<ConnectGoogleStepProps> = ({
  onNext,
  onBack,
  onGoogleAccountCreated,
}) => {
  const { t } = useTranslation('wizard');
  const { user } = useAuth();
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleGoogleConnect = async () => {
    if (!user) return;

    setIsConnecting(true);
    setError(null);

    const popup = window.open(
      'about:blank',
      'google-oauth',
      'width=500,height=600,scrollbars=yes,resizable=yes'
    );

    try {
      const redirectUri = `${window.location.origin}/auth/google/callback`;
      // Client-ID via gemeinsamen Helper (Retry + localStorage-Fallback, Sentry DENO-T);
      // die lokalisierte Fehlermeldung dieses Steps bleibt erhalten.
      let clientId: string;
      try {
        clientId = await getGoogleClientId();
      } catch {
        throw new Error(t('connect.failedClientId'));
      }
      const scope = [
        'https://www.googleapis.com/auth/content',
        'openid',
        'https://www.googleapis.com/auth/userinfo.email',
        'https://www.googleapis.com/auth/userinfo.profile'
      ].join(' ');

      const authUrl = new URL('https://accounts.google.com/o/oauth2/v2/auth');
      authUrl.searchParams.set('client_id', clientId);
      authUrl.searchParams.set('redirect_uri', redirectUri);
      authUrl.searchParams.set('response_type', 'code');
      authUrl.searchParams.set('scope', scope);
      authUrl.searchParams.set('state', user.id);
      authUrl.searchParams.set('access_type', 'offline');
      authUrl.searchParams.set('prompt', 'consent');
      authUrl.searchParams.set('include_granted_scopes', 'true');

      if (popup && !popup.closed) {
        popup.location.href = authUrl.toString();
      } else {
        window.location.href = authUrl.toString();
        return;
      }

      const handleMessage = (event: MessageEvent) => {
        if (event.origin !== window.location.origin) return;
        
        if (event.data.type === 'GOOGLE_OAUTH_SUCCESS') {
          popup?.close();
          setIsConnecting(false);
          const accountId = event.data.data.googleAccountId;
          onGoogleAccountCreated(accountId);
          window.removeEventListener('message', handleMessage);
        } else if (event.data.type === 'GOOGLE_OAUTH_ERROR') {
          popup?.close();
          // Findings R2-3 + R3-3, bewusst ohne Codeauswertung: dieser Schritt zeigt nur
          // eine Alert-Zeile ohne eigene Ueberschrift, und GoogleCallback legt in
          // `event.data.error` bereits den fertig uebersetzten, ursachengenauen Satz ab
          // ("Google antwortet gerade nicht ...", "... setze das Haekchen ..."). Eine
          // zweite Zuordnung ueber resolveOAuthErrorCopy waere hier reine Doppelung.
          // Geprueft, nicht uebersehen.
          setError(event.data.error || t('connect.failedConnect'));
          setIsConnecting(false);
          window.removeEventListener('message', handleMessage);
        }
      };

      window.addEventListener('message', handleMessage);

      const checkClosed = setInterval(() => {
        if (popup?.closed) {
          clearInterval(checkClosed);
          setIsConnecting(false);
          window.removeEventListener('message', handleMessage);
        }
      }, 500);

    } catch (err) {
      popup?.close();
      setError(err instanceof Error ? err.message : t('connect.errorOccurred'));
      setIsConnecting(false);
    }
  };

  return (
    <WizardStep
      title={t('connect.title')}
      description={t('connect.description')}
      onBack={onBack}
      hideNext
    >
      <div className="space-y-4 flex flex-col items-center justify-center py-4">
        {error && (
          <Alert variant="destructive" className="w-full">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="text-center space-y-3">
          <div className="w-14 h-14 mx-auto bg-primary/10 rounded-full flex items-center justify-center">
            <svg className="w-7 h-7" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
          </div>

          <p className="text-sm text-muted-foreground">
            {t('connect.authorize')}
          </p>

          {/* Bericht 3.7: Das OAuth-Popup darf nicht ohne Erklaerung aufgehen.
              Der Block steht bewusst STATISCH vor dem Button (Muster aus
              WelcomeStep) — kein Zwischenklick/Modal, damit window.open im
              Klick-Handler synchron bleibt (Popup-Blocker). */}
          <div className="w-full max-w-md mx-auto rounded-lg border bg-muted/30 p-3 space-y-2.5 text-left">
            <div className="flex items-start gap-2.5">
              <Eye className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
              <span className="text-xs text-muted-foreground">{t('connect.scopeAccess')}</span>
            </div>
            <div className="flex items-start gap-2.5">
              <ShieldCheck className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
              <span className="text-xs text-muted-foreground">{t('connect.scopeUntouched')}</span>
            </div>
            <div className="flex items-start gap-2.5">
              <Undo2 className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
              <span className="text-xs text-muted-foreground">{t('connect.scopeRevoke')}</span>
            </div>
          </div>

          <Button
            onClick={handleGoogleConnect}
            disabled={isConnecting}
            className="min-w-[180px]"
          >
            {isConnecting ? (
              <>
                <Spinner size="sm" />
                {t('connect.connecting')}
              </>
            ) : (
              t('connect.connectButton')
            )}
          </Button>
        </div>
      </div>
    </WizardStep>
  );
};

export default ConnectGoogleStep;
